/* PinPlacer (Phase 112): lets a resident (or admin) mark the exact spot of an
   issue. Click to place, drag to adjust, "Use my location", or look up an
   address/intersection to jump the map there. Reports the chosen point and
   whether the resident wants it shown exactly (public place) or generalized.
   The exact point is only ever sent to the server; the public map shows the
   generalized position unless "public place" is ticked. */
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Check, Crosshair, Loader2, LocateFixed, Maximize2, MapPin, Minimize2, Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { api } from '../lib/api';
import { pushpinIcon } from './MapPushpin';

const CKL_CENTER = [44.35, -78.74];

const ClickCapture = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
};

// Re-measure the map after the container changes size (fullscreen toggle).
const ResizeOnChange = ({ token }) => {
  const map = useMap();
  useEffect(() => {
    const t1 = setTimeout(() => map.invalidateSize(), 60);
    const t2 = setTimeout(() => map.invalidateSize(), 350);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [token, map]);
  return null;
};

const FlyTo = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), target.zoom || 16), { duration: 0.6 });
  }, [target, map]);
  return null;
};

export const PinPlacer = ({ value, onChange, exactPublic = false, onExactPublicChange, showExactOption = true, height = 'h-72', testId = 'pin-placer', helpText }) => {
  const [target, setTarget] = useState(value ? { ...value, zoom: 16 } : null);
  const [locating, setLocating] = useState(false);
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [hits, setHits] = useState([]);
  const [msg, setMsg] = useState('');
  const [full, setFull] = useState(false);
  const [coordOpen, setCoordOpen] = useState(false);
  const [coordLat, setCoordLat] = useState('');
  const [coordLng, setCoordLng] = useState('');
  const seq = useRef(0);

  // Typed coordinates (desktop or mobile): "44.1821, -78.5683" or two fields.
  const applyCoords = () => {
    let a = coordLat.trim(); let b = coordLng.trim();
    if (a.includes(',') && !b) { [a, b] = a.split(',').map((x) => x.trim()); }
    const lat = Number(a); const lng = Number(b);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) { setMsg('Enter a latitude and longitude like 44.1821, -78.5683.'); return; }
    pick(lat, lng);
    setTarget({ lat, lng, zoom: 17 });
    setCoordOpen(false);
  };

  const pick = (lat, lng) => { onChange({ lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) }); setMsg(''); };

  // Fullscreen is a CSS overlay (works on iOS too) so all form state stays put.
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFull(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [full]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { setMsg('Your browser does not share location. Click the map instead.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { pick(pos.coords.latitude, pos.coords.longitude); setTarget({ lat: pos.coords.latitude, lng: pos.coords.longitude, zoom: 17 }); setLocating(false); },
      () => { setMsg('Could not get your location. Click the map to place the pin.'); setLocating(false); },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  };

  const search = async () => {
    const query = q.trim();
    if (query.length < 4) return;
    const my = ++seq.current;
    setSearching(true);
    setHits([]);
    try {
      const r = await api.post('/ward-finder/geocode', { q: query });
      if (my !== seq.current) return;
      const s = r.data?.suggestions || [];
      setHits(s);
      if (!s.length) setMsg(r.data?.hint || 'No match. Zoom in and click the exact spot on the map instead.');
    } catch {
      if (my === seq.current) setMsg('Address lookup is busy. Click the map to place the pin.');
    } finally {
      if (my === seq.current) setSearching(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative sm:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }} placeholder="Jump to an address or intersection (optional)" className="h-11 pl-9" data-testid={`${testId}-search-input`} />
        </div>
        <Button type="button" variant="outline" onClick={search} disabled={searching || q.trim().length < 4} className="h-11 font-bold btn-press" data-testid={`${testId}-search-button`}>
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Find'}
        </Button>
        <Button type="button" variant="outline" onClick={useMyLocation} disabled={locating} className="h-11 font-bold btn-press" data-testid={`${testId}-locate-button`}>
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4 mr-1.5" />} Use my location
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button type="button" onClick={() => setCoordOpen((v) => !v)} className="font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-2 min-h-9" data-testid={`${testId}-coords-toggle`}>{coordOpen ? 'Hide coordinate entry' : 'Enter coordinates instead'}</button>
      </div>
      {coordOpen && (
        <div className="flex flex-col sm:flex-row gap-2" data-testid={`${testId}-coords-form`}>
          <Input value={coordLat} onChange={(e) => setCoordLat(e.target.value)} placeholder="Latitude (e.g. 44.1821) or lat, lng" className="h-11 sm:flex-1" inputMode="decimal" data-testid={`${testId}-coords-lat`} />
          <Input value={coordLng} onChange={(e) => setCoordLng(e.target.value)} placeholder="Longitude (e.g. -78.5683)" className="h-11 sm:flex-1" inputMode="decimal" data-testid={`${testId}-coords-lng`} />
          <Button type="button" variant="outline" onClick={applyCoords} className="h-11 font-bold btn-press" data-testid={`${testId}-coords-apply`}>Place pin here</Button>
        </div>
      )}
      {hits.length > 0 && (
        <ul className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 max-h-40 overflow-y-auto" data-testid={`${testId}-hits`}>
          {hits.map((h, i) => (
            <li key={`${h.lat}-${h.lng}-${i}`}>
              <button type="button" className="w-full text-left px-3 py-2 text-sm text-[#1a2744] hover:bg-slate-50 min-h-10" onClick={() => { setTarget({ lat: h.lat, lng: h.lng, zoom: 17 }); pick(h.lat, h.lng); setHits([]); }} data-testid={`${testId}-hit-${i}`}>
                <MapPin className="inline h-3.5 w-3.5 mr-1 text-[#C9A84C]" />{h.label}{h.ward ? <span className="ml-1.5 text-[10px] font-bold text-slate-500">Ward {h.ward}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className={full ? 'fixed inset-0 z-[1100] bg-white flex flex-col' : ''} data-testid={`${testId}-stage`} data-fullscreen={full ? 'true' : 'false'}>
        {full && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 bg-white">
            <p className="text-sm font-bold text-[#1a2744] truncate"><MapPin className="inline h-4 w-4 text-[#C9A84C] mr-1" /> {value ? `Pin at ${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}` : 'Click or tap the exact spot, then press Use This Location'}</p>
            <div className="flex gap-2 shrink-0">
              <Button type="button" onClick={() => setFull(false)} disabled={!value} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`${testId}-use-location`}><Check className="h-4 w-4 mr-1.5" /> Use This Location</Button>
              <Button type="button" variant="outline" onClick={() => setFull(false)} className="h-11 font-bold btn-press" aria-label="Exit full screen" data-testid={`${testId}-exit-fullscreen`}><Minimize2 className="h-4 w-4 mr-1.5" /> Exit</Button>
            </div>
          </div>
        )}
      <div className={`${full ? 'flex-1 min-h-0' : `${height} rounded-xl border border-slate-200`} w-full overflow-hidden relative`} data-testid={`${testId}-map`}>
        <MapContainer center={value ? [value.lat, value.lng] : CKL_CENTER} zoom={value ? 16 : 10} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickCapture onPick={pick} />
          <ResizeOnChange token={full} />
          <FlyTo target={target} />
          {value && (
            <Marker position={[value.lat, value.lng]} icon={pushpinIcon('#b91c1c')} draggable eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); pick(p.lat, p.lng); } }} />
          )}
        </MapContainer>
        {!full && (
          <button type="button" onClick={() => setFull(true)} className="absolute right-2 top-2 z-[400] h-11 px-3 inline-flex items-center gap-1.5 rounded-lg bg-white/95 border border-slate-300 shadow text-xs font-bold text-[#1a2744] hover:bg-white" aria-label="Enlarge map to full screen" data-testid={`${testId}-fullscreen`}>
            <Maximize2 className="h-4 w-4" /> Enlarge Map
          </button>
        )}
        {!value && (
          <div className="pointer-events-none absolute left-16 right-32 top-2 flex justify-center z-[400]">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1a2744]/90 text-white text-xs font-bold px-3 py-1.5 shadow text-center" data-testid={`${testId}-prompt`}><Crosshair className="h-3.5 w-3.5" /> Zoom in and click the exact spot of the issue</span>
          </div>
        )}
      </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        {value ? (
          <span className="inline-flex items-center gap-1.5 text-emerald-800 font-bold" data-testid={`${testId}-coords`}><MapPin className="h-3.5 w-3.5" /> Pin placed at {value.lat.toFixed(5)}, {value.lng.toFixed(5)}. Drag it to adjust.</span>
        ) : (
          <span className="text-slate-500" data-testid={`${testId}-empty`}>{helpText || 'No pin yet. Click the map, use your location, or look up an address.'}</span>
        )}
        {value && (
          <button type="button" onClick={() => { onChange(null); setMsg(''); }} className="inline-flex items-center gap-1 text-slate-500 hover:text-red-700 font-bold min-h-9" data-testid={`${testId}-clear`}><X className="h-3.5 w-3.5" /> Remove pin</button>
        )}
      </div>
      {msg && <p className="text-xs text-amber-800" data-testid={`${testId}-message`}>{msg}</p>}
      {showExactOption && value && (
        <div className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <Checkbox id={`${testId}-exact`} checked={exactPublic} onCheckedChange={(v) => onExactPublicChange?.(!!v)} className="mt-0.5" data-testid={`${testId}-exact-checkbox`} />
          <Label htmlFor={`${testId}-exact`} className="text-xs font-normal text-slate-700 leading-relaxed cursor-pointer">
            <strong>This is a public place</strong> (road, intersection, park, ditch, sign). Show the public pin exactly here.
            Leave unticked for a home or private property: the public pin is then placed about 100 to 150 m away to protect the address, while Steve still sees the exact spot.
          </Label>
        </div>
      )}
    </div>
  );
};
